/**
 * /assets/import — asset & category CSV import (ADR-0024 Phase 4).
 *
 * The employee-import UI's failure (a bare text-link template, no format
 * guidance) is deliberately NOT repeated: the template download is a sequenced
 * PRIMARY step, and the expected columns are shown inline (required vs optional,
 * create-mode vs assign-mode per the 2b template).
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { downloadImportTemplate, uploadImport, commitImport, cancelImport, ASSET_IMPORT_COLUMNS, CATEGORY_IMPORT_COLUMNS, type ImportKind, type ImportRecord } from '../api/assets.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { TabButton } from './assetShared.js';

export const assetImportRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/assets/import',
  component: AssetImportPage,
});

function AssetImportPage(): JSX.Element {
  const [kind, setKind] = useState<ImportKind>('asset');
  const [record, setRecord] = useState<ImportRecord | null>(null);
  const [committed, setCommitted] = useState<number | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploadImport(kind, file),
    onSuccess: (r) => { setRecord(r); setCommitted(null); },
  });
  const commit = useMutation({
    mutationFn: () => commitImport(record!.id),
    onSuccess: (r) => { setCommitted(r.committedCount); setRecord(null); },
  });
  const cancel = useMutation({ mutationFn: () => cancelImport(record!.id), onSuccess: () => { setRecord(null); } });

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">Bulk import</h1>
        <p className="text-sm text-gray-400">Import asset categories or assets (with optional custody) from a CSV.</p>
      </header>

      <div className="flex gap-1.5">
        <TabButton active={kind === 'category'} onClick={() => { setKind('category'); setRecord(null); }}>Categories</TabButton>
        <TabButton active={kind === 'asset'} onClick={() => { setKind('asset'); setRecord(null); }}>Assets</TabButton>
      </div>

      {/* Step 1 — the template, as a primary sequenced step (not a buried link). */}
      <ol className="space-y-4">
        <li className="rounded border border-gray-900 p-4 space-y-2">
          <div className="text-sm text-gray-300"><span className="font-medium">Step 1.</span> Download the template and fill it in.</div>
          <button type="button" onClick={() => void downloadImportTemplate(kind)} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200">
            ⬇ Download {kind} template
          </button>
          <ColumnsDisclosure kind={kind} />
        </li>

        <li className="rounded border border-gray-900 p-4 space-y-2">
          <div className="text-sm text-gray-300"><span className="font-medium">Step 2.</span> Upload your filled CSV for a dry run.</div>
          <input type="file" accept=".csv,text/csv" aria-label="CSV file" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); }} className="block text-sm text-gray-300" />
          {upload.isPending ? <p className="text-sm text-gray-400">Checking…</p> : null}
          <ApiErrorNotice error={upload.error ?? commit.error} fallback="Something went wrong with the import." />
        </li>
      </ol>

      {committed !== null ? (
        <div role="status" className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">Imported {committed} row{committed === 1 ? '' : 's'}.</div>
      ) : null}

      {record ? (
        <div className="rounded border border-gray-900 p-4 space-y-3">
          <h2 className="text-sm font-medium text-gray-200">Dry-run result</h2>
          <p className="text-sm text-gray-300">{record.validRowCount} valid · <span className={record.errorRowCount > 0 ? 'text-red-400' : ''}>{record.errorRowCount} error{record.errorRowCount === 1 ? '' : 's'}</span> of {record.rowCount} rows.</p>
          {record.errors.length > 0 ? (
            <div className="space-y-1">
              <div className="text-xs uppercase tracking-wide text-gray-500">Row errors</div>
              <ul className="text-xs text-red-300 space-y-0.5 max-h-64 overflow-y-auto">
                {record.errors.map((e, i) => <li key={i}><span className="font-mono text-gray-500">row {e.row}</span> [{e.category}] {e.message}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => commit.mutate()} disabled={record.errorRowCount > 0 || commit.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50" title={record.errorRowCount > 0 ? 'Fix the row errors and re-upload before committing.' : undefined}>
              Commit {record.validRowCount} row{record.validRowCount === 1 ? '' : 's'}
            </button>
            <button type="button" onClick={() => cancel.mutate()} className="rounded border border-gray-700 text-gray-300 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ColumnsDisclosure({ kind }: { kind: ImportKind }): JSX.Element {
  const columns = kind === 'category' ? CATEGORY_IMPORT_COLUMNS : ASSET_IMPORT_COLUMNS;
  return (
    <details className="text-xs text-gray-400">
      <summary className="cursor-pointer text-gray-300">Expected columns</summary>
      <table className="mt-2 w-full">
        <thead className="text-left text-gray-500"><tr><th className="py-1 pr-3">Column</th><th className="py-1 pr-3">Required</th><th className="py-1">Notes</th></tr></thead>
        <tbody>
          {columns.map((c) => (
            <tr key={c.name} className="border-t border-gray-900"><td className="py-1 pr-3 font-mono text-gray-300">{c.name}</td><td className="py-1 pr-3">{c.required}</td><td className="py-1">{c.note}</td></tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}
